/**
 * SliceDependentGrid — 1x2 layout for the `slice_dependent` gating mode.
 *
 * Renders an annotating axial viewport on the left and a coplanar thick-slab
 * MIP viewport on the right, both bound to the same axial series. The MIP
 * shares the volume texture with the axial (Cornerstone caches by volumeId)
 * so there's no extra GPU memory cost, and its slab thickness + projection
 * mode come from `slice_dependent_config` on the worklist.
 *
 * Camera sync (full focal/pan/zoom mirror) lives in `useSliceDependentSync`.
 * Slice navigation flows through the shared `currentSliceAtom` — both
 * viewports use the default `useGlobalSliceState=true`, so when one fires
 * VOLUME_NEW_IMAGE it updates the atom and the other viewport's effect
 * jumps to match. Same atom is what SegmentationAnnotationPanel writes to navigate
 * to a finding, so that flow keeps working in this mode too. The
 * SliceNavigator scrollbars read/write the same atom.
 */
import React from "react";
import { useAtomValue, useSetAtom } from "jotai";

import VolumeViewport from "../VolumeViewport";
import { StudyMetadataResponse } from "../../utils/dicomMetadataLoader";
import { sliceDependentConfigAtom } from "../../atoms/labelingAtoms";
import { currentSliceAtom, totalSlicesAtom } from "../../atoms/selectionAtoms";
import { useSliceDependentSync } from "../../hooks/useSliceDependentSync";
import { ViewerError } from "../common";
import { SliceNavigator } from "./SliceNavigator";

export interface SliceDependentGridProps {
  studyId: string;
  studyMetadata: StudyMetadataResponse;
  /** Series ID picked by the user (or auto-selected). Required — the
   *  SeriesPicker path in LabelingPage gates rendering on this. */
  axialSeriesId: string;
}

const AXIAL_VIEWPORT_ID = "sliceDependent_axial";
const MIP_VIEWPORT_ID = "sliceDependent_mip";

export const SliceDependentGrid: React.FC<SliceDependentGridProps> = ({
  studyId,
  studyMetadata,
  axialSeriesId,
}) => {
  // All hooks run unconditionally — early return below comes AFTER hook calls
  // so React's hook-order invariant holds across the null→loaded transition.
  const sliceConfig = useAtomValue(sliceDependentConfigAtom);
  const sliceIndex = useAtomValue(currentSliceAtom);
  const totalSlices = useAtomValue(totalSlicesAtom);
  const setCurrentSlice = useSetAtom(currentSliceAtom);
  useSliceDependentSync(AXIAL_VIEWPORT_ID, MIP_VIEWPORT_ID);

  // The backend echoes a fully-merged config whenever gating_mode is
  // slice_dependent. If we got here without one the worklist payload is
  // malformed — surface explicitly rather than silently falling back.
  if (!sliceConfig) {
    return (
      <ViewerError
        title="Missing slice_dependent_config"
        message="Worklist is in slice_dependent mode but the backend did not provide slice_dependent_config. Check the worklist JSON or backend logs."
      />
    );
  }

  const mipBlend = sliceConfig.mip_blend_mode === "MinIP" ? "MinIP" : "MIP";
  const mipLabel =
    sliceConfig.mip_blend_mode === "MinIP"
      ? `Axial MinIP ${sliceConfig.mip_slab_thickness_mm}mm`
      : `Axial MIP ${sliceConfig.mip_slab_thickness_mm}mm`;

  return (
    <div className="w-full h-full grid grid-cols-2 gap-1 bg-black">
      <div className="relative w-full h-full">
        <div className="absolute top-2 left-2 z-10 text-xs text-white/80 bg-black/40 px-2 py-1 rounded">
          Axial
        </div>
        <VolumeViewport
          seriesId={axialSeriesId}
          studyMetadata={studyMetadata}
          studyId={studyId}
          viewportIdOverride={AXIAL_VIEWPORT_ID}
          viewportMode="annotate"
        />
        <SliceNavigator
          sliceIndex={sliceIndex}
          totalSlices={totalSlices}
          onSliceChange={setCurrentSlice}
          isActive
        />
      </div>
      <div className="relative w-full h-full">
        <div className="absolute top-2 left-2 z-10 text-xs text-white/80 bg-black/40 px-2 py-1 rounded">
          {mipLabel}
        </div>
        <VolumeViewport
          seriesId={axialSeriesId}
          studyMetadata={studyMetadata}
          studyId={studyId}
          viewportIdOverride={MIP_VIEWPORT_ID}
          viewportMode="readonly"
          blendMode={mipBlend}
          slabThicknessMm={sliceConfig.mip_slab_thickness_mm}
        />
        <SliceNavigator
          sliceIndex={sliceIndex}
          totalSlices={totalSlices}
          onSliceChange={setCurrentSlice}
          isActive
        />
      </div>
    </div>
  );
};

export default SliceDependentGrid;
